import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { areasAPI, camerasAPI, modelsAPI, logsAPI, notifyNotificationsChanged } from '../../utils/api';
import { useAuthenticatedBlobUrl } from '../../utils/useAuthenticatedBlobUrl';
// import { usePeopleCountWebSocket } from '../../utils/usePeopleCountWebSocket';
import {
  cameraHasPeopleCounterModel,
  isPeopleCounterModel,
} from '../../utils/peopleCounter';
import Loader from '../../components/Loader/Loader';
import RoiSelector from '../../components/RoiSelector/RoiSelector';
import PresenceSettingsModal from '../../components/PresenceSettingsModal/PresenceSettingsModal';
import DirectionVectorSelector from '../../components/DirectionVectorSelector/DirectionVectorSelector';
import AreaPlatesModal from '../../components/AreaPlatesModal/AreaPlatesModal';
import ResidentsModal from '../../components/ResidentsModal/ResidentsModal';
import { useConfirm } from '../../components/ConfirmDialog/ConfirmDialog';
import BarrierModal from '../../components/BarrierModal/BarrierModal';
import {
  cameraHasLicensePlateModel,
  findLicensePlateModel,
  hasLicensePlateRoi,
  DirectionVector,
  drawDirectionArrow,
  parseDirectionVector,
} from '../../utils/directionVector';
import {
  cameraHasPresenceCheckModel,
  DEFAULT_PRESENCE_SETTINGS,
  isPresenceCheckModel,
  normalizePresenceSettings,
  PresenceZone,
} from '../../utils/presence';
import LogsDateRangePicker from '../../components/LogsDateRangePicker/LogsDateRangePicker';
import DetectionsStatsModal from '../../components/DetectionsStatsModal/DetectionsStatsModal';
import PresenceStatsModal from '../../components/PresenceStatsModal/PresenceStatsModal';
import { getModelDisplayName } from '../../utils/modelLabels';
import { DirectionBadge } from '../../components/DirectionBadge/DirectionBadge';
import { cameraBillingAPI, isInsufficientFundsError, type PlateDirection } from '../../utils/api';
import { isSubscriptionRequiredError } from '../../utils/subscriptionErrors';
import './Area.css';

interface AreaData {
  id: number;
  name: string;
  description: string | null;
}

/** Дата окончания оплаты / удаления камеры: «10 октября 2026». */
const formatBillingDate = (value: string | null | undefined): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
};

type AreaLogItem = {
  id: number;
  timestamp: string;
  model: string;
  screen_url: string | null;
  json_data: any;
  camera_id?: number | null;
  /** Направление проезда (LPR): entry | exit | unknown; null/undefined — не применимо. */
  direction?: PlateDirection | null;
  zone_name?: string | null;
  absence_duration_sec?: number | null;
};

/** Обновляет первую страницу логов, сохраняя подгруженные через «Показать ещё». */
const mergeFreshLogsHead = (
  prev: AreaLogItem[],
  fresh: AreaLogItem[],
  firstPageSize: number
): AreaLogItem[] => {
  if (prev.length <= firstPageSize) {
    return fresh;
  }
  return dedupeLogsById([...fresh, ...prev.slice(firstPageSize)]);
};

/** Убираем повторы по id (голова списка могла сдвинуться между запросами страниц). */
const dedupeLogsById = (items: AreaLogItem[]): AreaLogItem[] => {
  const seen = new Set<number | string>();
  const out: AreaLogItem[] = [];
  for (const item of items) {
    const key = item.id ?? `${item.timestamp}|${item.model}|${item.camera_id ?? ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
};

const Area: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading: authLoading, user, refreshUser } = useAuth();
  const isCameraWaiting = (camera?: {
    webrtc_out?: string | null;
    sync_status?: string | null;
  } | null) => {
    if (!camera) return true;
    const sync = camera.sync_status ?? null;
    if (
      sync === 'pending_create' ||
      sync === 'pending_update' ||
      sync === 'pending_delete'
    ) {
      return true;
    }
    return !camera.webrtc_out;
  };
  const cameraIconSvgUrl = `${process.env.PUBLIC_URL}/icons/svg_icon/camera.svg`;
  const roiIconSvgUrl = `${process.env.PUBLIC_URL}/icons/svg_icon/roi.svg`;
  const presenceIconSvgUrl = `${process.env.PUBLIC_URL}/icons/modals/people.svg`;
  const directionVectorIconSvgUrl = `${process.env.PUBLIC_URL}/icons/svg_icon/direction-vector.svg`;
  const platesIconSvgUrl = `${process.env.PUBLIC_URL}/icons/svg_icon/plates.svg`;
  const residentsIconSvgUrl = `${process.env.PUBLIC_URL}/icons/svg_icon/residents.svg`;
  const barrierIconSvgUrl = `${process.env.PUBLIC_URL}/icons/svg_icon/barrier.svg`;
  const modalIconsBase = `${process.env.PUBLIC_URL}/icons/modals`;

  const isForbiddenOrNotFound = React.useCallback((err: any) => {
    if (isSubscriptionRequiredError(err)) {
      return false;
    }
    const status = err?.status ?? err?.response?.status;
    return status === 403 || status === 404;
  }, []);

  const handleSubscriptionLost = React.useCallback(() => {
    navigate('/dashboard', { replace: true });
  }, [navigate]);

  const handleApiAccessError = React.useCallback(
    (err: unknown): boolean => {
      if (isSubscriptionRequiredError(err)) {
        handleSubscriptionLost();
        return true;
      }
      if (isForbiddenOrNotFound(err)) {
        navigate('/dashboard', { replace: true });
        return true;
      }
      return false;
    },
    [handleSubscriptionLost, isForbiddenOrNotFound, navigate]
  );

  const getCameraIdentifier = React.useCallback(
    (c: { id: number | null; camera_id: string | null; name: string }) => String(c.id ?? c.camera_id ?? c.name),
    []
  );

  const [area, setArea] = useState<AreaData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [cameras, setCameras] = useState<Array<{ 
    id: number | null; 
    name: string; 
    rtsp_uri: string; 
    models: string[]; 
    camera_id: string | null;
    hls_out?: string | null;
    webrtc_out?: string | null;
    sync_status?: string | null;
    status?: any;
    /** false — камеры нет в статусе внешнего API (отключена / нет видеопотока). */
    is_online?: boolean;
    /** Оплата камеры (1 камера — подписка в месяц). */
    is_paid?: boolean;
    paid_until?: string | null;
    auto_renew?: boolean;
    delete_at?: string | null;
  }>>([]);
  const [camerasLoading, setCamerasLoading] = useState(false);
  const presenceStatsCameras = useMemo(() => cameras.filter((camera) => cameraHasPresenceCheckModel(camera.models)), [cameras]);
  const [isPresenceStatsOpen, setIsPresenceStatsOpen] = useState(false);
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [modelTitles, setModelTitles] = useState<Record<string, string>>({});
  const [modelsLoading, setModelsLoading] = useState(false);
  const [logs, setLogs] = useState<AreaLogItem[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsLoadingMore, setLogsLoadingMore] = useState(false);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPerPage] = useState(10); // Первая загрузка - 10 логов
  const [logsPerPageMore] = useState(20); // При "показать еще" - 20 логов
  /**
   * Курсор «Показать ещё»: номер следующей страницы (по logsPerPageMore).
   * Считать страницу от logs.length нельзя: новые детекции сдвигают смещения,
   * а mergeFreshLogsHead добавляет их в голову списка — пагинация зацикливалась.
   */
  const logsNextPageRef = useRef(1);
  /** null — все камеры участка; иначе id камеры в БД */
  const [logsCameraFilterId, setLogsCameraFilterId] = useState<number | null>(null);
  /** null — все модели; иначе значение `logs.model` */
  const [logsModelFilter, setLogsModelFilter] = useState<string | null>(null);
  /** YYYY-MM-DD; пустая строка — без границы */
  const [logsDateFrom, setLogsDateFrom] = useState('');
  const [logsDateTo, setLogsDateTo] = useState('');

  /**
   * Ключ «для какого участка и фильтров сейчас показываются логи».
   * Ответ любого запроса логов применяем, только если ключ не изменился, пока запрос летел:
   * иначе при переходе в другой участок (или смене фильтра) запоздавший ответ
   * подменял список детекциями старого участка.
   */
  const logsContextKey = `${id ?? ''}|${logsCameraFilterId ?? ''}|${logsModelFilter ?? ''}|${logsDateFrom}|${logsDateTo}`;
  const logsContextKeyRef = useRef(logsContextKey);
  logsContextKeyRef.current = logsContextKey;
  /** Участок из URL уже загружен (при переходе area какое-то время указывает на предыдущий). */
  const isAreaCurrent = area != null && String(area.id) === String(id);
  const [logsSort, setLogsSort] = useState<{ key: 'timestamp' | 'model'; dir: 'asc' | 'desc' }>({
    key: 'timestamp',
    dir: 'desc',
  });
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [selectedPhotoUrl, setSelectedPhotoUrl] = useState<string | null>(null);
  /** Что показать под фото: модель/номер и направление (для LPR). */
  const [selectedPhotoMeta, setSelectedPhotoMeta] = useState<{
    label: string;
    direction: PlateDirection | null;
  } | null>(null);
  const {
    blobUrl: selectedPhotoBlobUrl,
    loading: selectedPhotoLoading,
    error: selectedPhotoError,
    onImageError: onSelectedPhotoImageError,
  } = useAuthenticatedBlobUrl(selectedPhotoUrl, isPhotoModalOpen);

  const [logsModels, setLogsModels] = useState<string[]>([]);
  const [isDetectionsStatsOpen, setIsDetectionsStatsOpen] = useState(false);

  const deepLinkOpenedRef = useRef(false);

  const deepLinkLogId = useMemo(() => {
    const sp = new URLSearchParams(location.search || '');
    const raw = sp.get('log') || sp.get('detection') || sp.get('log_id');
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [location.search]);

  useEffect(() => {
    deepLinkOpenedRef.current = false;
  }, [deepLinkLogId]);

  useEffect(() => {
    if (deepLinkOpenedRef.current) return;
    if (!deepLinkLogId || !area?.id) return;

    const openLogModal = (item: { id: number; screen_url: string | null; model?: string; direction?: PlateDirection | null }) => {
      deepLinkOpenedRef.current = true;
      if (item.screen_url) {
        setSelectedPhotoUrl(item.screen_url);
        setSelectedPhotoMeta(
          item.model ? { label: getModelDisplayName(item.model), direction: item.direction ?? null } : null
        );
        setIsPhotoModalOpen(true);
      }
    };

    const inList = logs.find((l) => l.id === deepLinkLogId);
    if (inList) {
      openLogModal(inList);
      return;
    }

    if (logsLoading) return;

    let cancelled = false;
    (async () => {
      try {
        const resp = await logsAPI.getById(area.id, deepLinkLogId);
        if (cancelled || !resp.success || !resp.log) return;

        setLogs((prev) => {
          if (prev.some((l) => l.id === resp.log!.id)) return prev;
          return [resp.log!, ...prev];
        });
        openLogModal(resp.log);
      } catch {
        // ignore: лог мог быть удалён или недоступен
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [deepLinkLogId, logs, logsLoading, area?.id]);

  useEffect(() => {
    const sp = new URLSearchParams(location.search || '');
    if (sp.get('openStats') === '1' && area?.id) {
      setIsDetectionsStatsOpen(true);
    }
  }, [location.search, area?.id]);

  /** Список моделей для фильтра — после sync логов в БД, иначе выпадающий список устаревает. */
  const loadLogsModels = React.useCallback(async () => {
    if (!area?.id || !isAreaCurrent) return;
    const contextKey = logsContextKeyRef.current;

    try {
      const resp = await logsAPI.getModelsByArea(area.id, {
        ...(logsCameraFilterId != null ? { cameraId: logsCameraFilterId } : {}),
        ...(logsDateFrom || logsDateTo
          ? { dateFrom: logsDateFrom || logsDateTo, dateTo: logsDateTo || logsDateFrom }
          : {}),
      });
      if (logsContextKeyRef.current !== contextKey) return;
      if (resp.success) {
        setLogsModels(resp.models || []);
      }
    } catch {
      // ignore: не блокируем страницу
    }
  }, [area?.id, isAreaCurrent, logsCameraFilterId, logsDateFrom, logsDateTo]);

  // Список моделей приходит с бэка из внешнего /api/cameras/{id}/models (имена моделей, не label'ы логов)
  const modelOptionsForArea = useMemo(() => {
    const keys = new Set<string>();
    logsModels.forEach((m) => {
      const k = (m || '').trim().toLowerCase();
      if (k) keys.add(k);
    });
    return Array.from(keys)
      .sort((a, b) => getModelDisplayName(a).localeCompare(getModelDisplayName(b), 'ru', { sensitivity: 'base' }))
      .map((value) => ({ value, label: getModelDisplayName(value) }));
  }, [logsModels]);

  useEffect(() => {
    if (logsModelFilter == null) return;
    const ok = modelOptionsForArea.some((o) => o.value === logsModelFilter);
    if (!ok) setLogsModelFilter(null);
  }, [logsModelFilter, modelOptionsForArea]);

  const sortedLogs = useMemo(() => {
    const dirMul = logsSort.dir === 'asc' ? 1 : -1;
    const byTime = (a: string, b: string) => (a === b ? 0 : a < b ? -1 : 1);
    const byText = (a: string, b: string) => a.localeCompare(b, 'ru', { sensitivity: 'base' });

    return [...logs].sort((a, b) => {
      if (logsSort.key === 'timestamp') {
        const cmp = byTime(a.timestamp, b.timestamp) * dirMul;
        if (cmp !== 0) return cmp;
        return byText(getModelDisplayName(a.model), getModelDisplayName(b.model)) * dirMul;
      }

      const cmp = byText(getModelDisplayName(a.model), getModelDisplayName(b.model)) * dirMul;
      if (cmp !== 0) return cmp;
      return byTime(a.timestamp, b.timestamp) * dirMul;
    });
  }, [logs, logsSort]);

  const toggleLogsSort = (key: 'timestamp' | 'model') => {
    setLogsSort((prev) => {
      if (prev.key !== key) return { key, dir: 'asc' };
      return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
    });
  };

  // Загрузка информации об участке
  useEffect(() => {
    // Ждем завершения авторизации перед загрузкой данных участка
    if (authLoading) {
      return;
    }

    const loadArea = async () => {
      if (!id) {
        navigate('/dashboard');
        return;
      }

      try {
        setLoading(true);
        const response = await areasAPI.getOne(Number(id));
        if (response.success && response.area) {
          setArea(response.area);
        } else {
          navigate('/dashboard', { replace: true });
        }
      } catch (err: any) {
        if (handleApiAccessError(err)) return;
        setError(err.message || 'Ошибка загрузки участка');
      } finally {
        setLoading(false);
      }
    };

    loadArea();
  }, [id, navigate, authLoading, handleApiAccessError]);

  useEffect(() => {
    setLogsCameraFilterId(null);
    setLogsModelFilter(null);
    setLogsDateFrom('');
    setLogsDateTo('');
    // Детекции и список моделей относятся к участку — не показываем их в чужом
    setLogs([]);
    setLogsTotal(0);
    setLogsModels([]);
    logsNextPageRef.current = 1;
    setIsPhotoModalOpen(false);
    setLprRoiOverride({});
  }, [id]);

  useEffect(() => {
    if (logsCameraFilterId == null) return;
    const stillThere = cameras.some((c) => c.id === logsCameraFilterId);
    if (!stillThere) setLogsCameraFilterId(null);
  }, [cameras, logsCameraFilterId]);

  // Загрузка камер для участка
  const loadCameras = React.useCallback(async () => {
    if (!area || !id) return;

    try {
      setCamerasLoading(true);
      console.log('Loading cameras for area:', id);
      const response = await camerasAPI.getByArea(Number(id));
      console.log('Cameras response:', response);
      if (response.success) {
        const nextCameras = response.cameras || [];
        setCameras(nextCameras);
        console.log('Cameras loaded:', response.cameras);

        // Если активная камера была удалена/изменилась — выбираем первую доступную,
        // иначе UI может показывать "дубли" (главная камера + та же в миниатюрах).
        setActiveCameraId((prev) => {
          if (nextCameras.length === 0) return null;
          const ids = new Set(nextCameras.map(getCameraIdentifier));
          if (prev === null) return getCameraIdentifier(nextCameras[0]);
          return ids.has(String(prev)) ? prev : getCameraIdentifier(nextCameras[0]);
        });
      } else {
        console.error('Failed to load cameras:', response);
      }
    } catch (err: any) {
      if (handleApiAccessError(err)) return;
      console.error('Ошибка загрузки камер:', err);
    } finally {
      setCamerasLoading(false);
    }
  }, [area, id, getCameraIdentifier, handleApiAccessError]);

  useEffect(() => {
    loadCameras();
  }, [loadCameras]);

  // Загрузка доступных моделей (аналитик)
  useEffect(() => {
    const loadModels = async () => {
      try {
        setModelsLoading(true);
        const response = await modelsAPI.getAll();
        if (response.success) {
          setAvailableModels((response.models || []).map((model) => model.name));
          setModelTitles(Object.fromEntries((response.models || []).map((model) => [model.name, model.title])));
        }
      } catch (err: any) {
        console.error('Ошибка загрузки моделей:', err);
      } finally {
        setModelsLoading(false);
      }
    };

    loadModels();
  }, []);

  const getLogsListOptions = React.useCallback(
    (extra?: { sync?: boolean; extMax?: number }): NonNullable<Parameters<typeof logsAPI.getByArea>[3]> => {
      const from = logsDateFrom || logsDateTo;
      const to = logsDateTo || logsDateFrom;
      return {
        ...extra,
        ...(logsCameraFilterId != null ? { cameraId: logsCameraFilterId } : {}),
        ...(logsModelFilter != null ? { model: logsModelFilter } : {}),
        ...(from ? { dateFrom: from, dateTo: to } : {}),
      };
    },
    [logsCameraFilterId, logsModelFilter, logsDateFrom, logsDateTo]
  );

  /** Тихая подкачка из внешнего API (не блокирует UI). */
  const syncLogsFromExternal = React.useCallback(async () => {
    if (!area?.id || !isAreaCurrent) return;
    const contextKey = logsContextKeyRef.current;

    const now = Date.now();
    const last = (window as any).__logsLastSyncAt as number | undefined;
    const syncCooldownMs = 15_000;
    if (last && now - last < syncCooldownMs) {
      return;
    }

    try {
      const response = await logsAPI.getByArea(
        area.id,
        1,
        logsPerPage,
        getLogsListOptions({ sync: true, extMax: 200 })
      );
      if (!response.success || logsContextKeyRef.current !== contextKey) return;

      (window as any).__logsLastSyncAt = now;
      const fresh = response.logs || [];
      setLogsTotal(response.total || 0);
      setLogs((prev) => mergeFreshLogsHead(prev, fresh, logsPerPage));
      await loadLogsModels();
    } catch (err) {
      handleApiAccessError(err);
    }
  }, [area?.id, isAreaCurrent, logsPerPage, getLogsListOptions, loadLogsModels, handleApiAccessError]);

  // Загрузка первых 10 логов участка — сначала быстро из БД (как «Показать ещё»), sync в фоне
  const loadLogs = React.useCallback(async () => {
    if (!area?.id || !isAreaCurrent) {
      return;
    }
    const contextKey = logsContextKeyRef.current;

    try {
      setLogsLoading(true);
      const response = await logsAPI.getByArea(
        area.id,
        1,
        logsPerPage,
        getLogsListOptions({ sync: false })
      );
      // Пока летел запрос, сменили участок или фильтр — ответ уже не наш
      if (logsContextKeyRef.current !== contextKey) return;
      logsNextPageRef.current = 1;
      if (response.success) {
        setLogs(response.logs || []);
        setLogsTotal(response.total || 0);
      } else {
        setLogs([]);
        setLogsTotal(0);
      }
    } catch (err: any) {
      if (handleApiAccessError(err)) return;
      if (logsContextKeyRef.current !== contextKey) return;
      setLogs([]);
      setLogsTotal(0);
    } finally {
      if (logsContextKeyRef.current === contextKey) {
        setLogsLoading(false);
      }
    }

    void loadLogsModels();
    void syncLogsFromExternal();
  }, [
    area?.id,
    isAreaCurrent,
    logsPerPage,
    getLogsListOptions,
    loadLogsModels,
    syncLogsFromExternal,
    handleApiAccessError,
  ]);

  // Тихое обновление логов (без сброса "показать еще")
  const refreshLogs = React.useCallback(async () => {
    if (!area?.id || !isAreaCurrent) return;
    const contextKey = logsContextKeyRef.current;

    const now = Date.now();
    const last = (window as any).__logsLastSyncAt as number | undefined;
    const syncCooldownMs = 15_000;
    const shouldSync = !last || now - last >= syncCooldownMs;

    try {
      const response = await logsAPI.getByArea(
        area.id,
        1,
        logsPerPage,
        getLogsListOptions({ sync: false })
      );
      if (!response.success || logsContextKeyRef.current !== contextKey) return;

      const fresh = response.logs || [];
      setLogsTotal(response.total || 0);
      setLogs((prev) => mergeFreshLogsHead(prev, fresh, logsPerPage));

      if (shouldSync) {
        void syncLogsFromExternal();
      }
    } catch (err) {
      handleApiAccessError(err);
    }
  }, [area?.id, isAreaCurrent, logsPerPage, getLogsListOptions, syncLogsFromExternal, handleApiAccessError]);

  // Загрузка дополнительных логов (по 20)
  const loadMoreLogs = React.useCallback(async () => {
    if (!area?.id || !isAreaCurrent || logsLoadingMore) return;
    const contextKey = logsContextKeyRef.current;

    const perPage = logsPerPageMore;
    const loadedIds = new Set(logs.map((l) => l.id));

    try {
      setLogsLoadingMore(true);

      // Страницы берём строго по возрастанию курсора; пересечения с уже загруженным
      // (первые 10 записей, сдвиг от свежих детекций) отсекаем по id.
      // Если страница целиком оказалась уже загруженной — сразу берём следующую.
      for (let attempt = 0; attempt < 3; attempt++) {
        const nextPage = logsNextPageRef.current;
        console.log('Loading more logs for area:', area.id, 'page:', nextPage, 'per_page:', perPage, 'loaded:', logs.length);
        // Подгрузка "Показать ещё": НЕ запускаем sync на бэке, иначе подгрузка будет очень медленной.
        const response = await logsAPI.getByArea(area.id, nextPage, perPage, getLogsListOptions({ sync: false }));
        if (logsContextKeyRef.current !== contextKey) return;
        const pageLogs = response.success && Array.isArray(response.logs) ? response.logs : [];
        if (pageLogs.length === 0) {
          console.log('No more logs to load');
          break;
        }

        logsNextPageRef.current = nextPage + 1;

        const logsToAdd = pageLogs.filter((l) => !loadedIds.has(l.id));
        if (logsToAdd.length > 0) {
          console.log('More logs loaded:', logsToAdd.length);
          setLogs((prevLogs) => dedupeLogsById([...prevLogs, ...logsToAdd]));
          break;
        }

        if (pageLogs.length < perPage) {
          console.log('No more logs to load (all already loaded)');
          break;
        }
        // вся страница — дубли, пробуем следующую
      }
    } catch (err: any) {
      if (handleApiAccessError(err)) return;
      console.error('Ошибка загрузки дополнительных логов:', err);
    } finally {
      setLogsLoadingMore(false);
    }
  }, [area?.id, isAreaCurrent, logs, logsLoadingMore, logsPerPageMore, getLogsListOptions, handleApiAccessError]);

  useEffect(() => {
    if (area) {
      loadLogs();
    }
  }, [area, loadLogs]);

  // Фоновая подгрузка логов без перезагрузки страницы
  useEffect(() => {
    if (!area?.id) return;

    const intervalId = window.setInterval(() => {
      refreshLogs();
    }, 15000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [area?.id, refreshLogs]);

  const [activeCameraId, setActiveCameraId] = useState<string | null>(null);
  const [isDeleteCameraOpen, setIsDeleteCameraOpen] = useState(false);
  const [videoReloadToken, setVideoReloadToken] = useState(0);
  const [isRoiSelectorOpen, setIsRoiSelectorOpen] = useState(false);
  const [isPresenceSettingsOpen, setIsPresenceSettingsOpen] = useState(false);
  const [presenceMonitoringEnabled, setPresenceMonitoringEnabled] = useState(false);
  const [presenceZone, setPresenceZone] = useState<PresenceZone[]>([]);
  const [isDirectionVectorOpen, setIsDirectionVectorOpen] = useState(false);
  const [isAreaPlatesOpen, setIsAreaPlatesOpen] = useState(false);
  const [isResidentsOpen, setIsResidentsOpen] = useState(false);
  /**
   * Зона ROI для модели номеров, только что сохранённая (true) или сброшенная (false) в RoiSelector:
   * статус внешнего API обновляется не мгновенно, а камеру нужно разблокировать сразу.
   */
  const [lprRoiOverride, setLprRoiOverride] = useState<Record<number, boolean>>({});
  /** Оплата камеры: автопродление с баланса (галка в форме добавления). */
  const [autoRenewNewCamera, setAutoRenewNewCamera] = useState(false);
  /** Последняя ошибка формы — нехватка денег: показываем ссылку на пополнение. */
  const [cameraFundsError, setCameraFundsError] = useState(false);
  const [isBarrierOpen, setIsBarrierOpen] = useState(false);
  const [directionVector, setDirectionVector] = useState<DirectionVector | null>(null);
  const mainVideoRef = useRef<HTMLDivElement>(null);
  const mainIframeRef = useRef<HTMLIFrameElement | null>(null);
  const roiOverlayCanvasRef = useRef<HTMLCanvasElement>(null);

  // Устанавливаем первую камеру как активную при загрузке
  useEffect(() => {
    if (cameras.length > 0 && activeCameraId === null) {
      setActiveCameraId(getCameraIdentifier(cameras[0]));
    }
  }, [cameras, activeCameraId, getCameraIdentifier]);

  const activeCamera = cameras.find((c) => getCameraIdentifier(c) === String(activeCameraId)) ?? cameras[0] ?? null;

  useEffect(() => {
    setPayCameraError(null);
  }, [activeCameraId]);

  /** Модели, реально привязанные к камере (БД). Не путать с `status.models` внешнего API — там зона ROI может ещё долго «висеть» после detach. */
  const attachedCameraModels = activeCamera?.models ?? [];
  // const activeHasPeopleModel = cameraHasPeopleCounterModel(attachedCameraModels);
  const activeHasPresenceModel = cameraHasPresenceCheckModel(attachedCameraModels);
  const activeHasLicensePlateModel = cameraHasLicensePlateModel(attachedCameraModels);
  /** Оплата камер: цена в месяц и хватает ли баланса на новую камеру. */
  const cameraMonthlyPrice = user?.camera_monthly_price ?? 1000;
  /** Неоплаченной камере настройки аналитики (ROI, вектор, шлагбаум…) не показываем. */
  const activeCameraUnpaid = !!activeCamera && activeCamera.is_paid === false;
  /** Модель распознавания номеров у активной камеры (для зоны ROI). */
  const activeLprModel = findLicensePlateModel(attachedCameraModels);

  /** Распознаванию номеров обязательна зона ROI: без неё камера заблокирована. */
  const cameraNeedsLprRoi = (camera?: {
    id?: number | null;
    models?: string[];
    status?: any;
    is_paid?: boolean;
    is_online?: boolean;
  } | null) => {
    if (!camera || camera.is_paid === false || camera.is_online === false) return false;
    if (!findLicensePlateModel(camera.models)) return false;
    const override = camera.id != null ? lprRoiOverride[camera.id] : undefined;
    const hasRoi = override ?? hasLicensePlateRoi(camera.status, camera.models);
    // null — по статусу не понять (ещё грузится): не блокируем
    return hasRoi === false;
  };
  const activeNeedsLprRoi = cameraNeedsLprRoi(activeCamera);
  const userBalance = user?.balance ?? 0;
  const canAffordCamera = userBalance >= cameraMonthlyPrice;
  // const [peopleCounterLiveEnabled, setPeopleCounterLiveEnabled] = useState(false);

  // Счётчик людей временно отключён (PEOPLE_COUNTER_ENABLED = false).
  // const showPeopleCounterPanel = Boolean(
  //   activeHasPeopleModel && activeCamera?.camera_id && peopleCounterLiveEnabled
  // );
  // const {
  //   count: peopleCount,
  //   connected: peopleWsConnected,
  //   error: peopleWsError,
  // } = usePeopleCountWebSocket(activeCamera?.camera_id ?? null, showPeopleCounterPanel);

  // Получаем размеры видео из статуса камеры
  const getVideoDimensions = () => {
    if (!activeCamera?.status) return { width: 1920, height: 1080 };

    const status = activeCamera.status;
    if (status.resolution?.width && status.resolution?.height) {
      return { width: status.resolution.width, height: status.resolution.height };
    } else if (status.src_width && status.src_height) {
      return { width: status.src_width, height: status.src_height };
    } else if (status.width && status.height) {
      return { width: status.width, height: status.height };
    }
    return { width: 1920, height: 1080 };
  };

  const videoDimensions = getVideoDimensions();

  // Отрисовка ROI оверлея на основном видео
  useEffect(() => {
    const canvas = roiOverlayCanvasRef.current;
    const container = mainVideoRef.current;
    if (!canvas || !container) return;

    // Всегда очищаем canvas при смене камеры/статуса — иначе может оставаться оверлей от предыдущей камеры,
    // когда у текущей еще нет roi данных.
    const clear = () => {
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    };

    clear();

    const roiData = activeCamera?.status?.roi;
    const boundModelSet = new Set(attachedCameraModels);
    const roiModelKeys =
      roiData && Object.keys(roiData).length > 0
        ? Object.keys(roiData).filter((modelName) => boundModelSet.has(modelName))
        : [];

    const hasPresenceZone = Boolean(presenceMonitoringEnabled && presenceZone.length);
    const hasDirectionVector = Boolean(activeHasLicensePlateModel && directionVector);

    if (roiModelKeys.length === 0 && !hasPresenceZone && !hasDirectionVector) {
      return;
    }

    const updateCanvasSize = () => {
      const iframe = container.querySelector('iframe');
      if (iframe) {
        const rect = iframe.getBoundingClientRect();
        canvas.width = rect.width;
        canvas.height = rect.height;
        canvas.style.width = rect.width + 'px';
        canvas.style.height = rect.height + 'px';
        drawRoiOverlay();
      } else {
        // если iframe еще не появился/переинициализируется — не оставляем старый рисунок
        clear();
      }
    };

    const drawRoiOverlay = () => {
      const ctx = canvas.getContext('2d');
      if (!ctx || canvas.width === 0 || canvas.height === 0) return;

      // Очищаем canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Вычисляем реальную область видео с учетом соотношения сторон
      const videoAspect = videoDimensions.width / videoDimensions.height;
      const canvasAspect = canvas.width / canvas.height;

      let videoDisplayWidth: number, videoDisplayHeight: number, offsetX: number, offsetY: number;

      if (canvasAspect > videoAspect) {
        // Canvas шире - letterboxing (черные полосы сверху/снизу)
        videoDisplayHeight = canvas.height;
        videoDisplayWidth = canvas.height * videoAspect;
        offsetX = (canvas.width - videoDisplayWidth) / 2;
        offsetY = 0;
      } else {
        // Canvas выше - pillarboxing (черные полосы слева/справа)
        videoDisplayWidth = canvas.width;
        videoDisplayHeight = canvas.width / videoAspect;
        offsetX = 0;
        offsetY = (canvas.height - videoDisplayHeight) / 2;
      }

      // Масштаб для конвертации координат видео в координаты canvas
      const scaleX = videoDisplayWidth / videoDimensions.width;
      const scaleY = videoDisplayHeight / videoDimensions.height;

      // Рисуем ROI только для моделей, которые всё ещё привязаны к камере
      roiModelKeys.forEach((modelName) => {
        const modelRoi = roiData[modelName];

        // Рисуем pre_crop (если есть)
        if (modelRoi.pre_crop && modelRoi.pre_crop.x !== null && modelRoi.pre_crop.y !== null) {
          const x = offsetX + (modelRoi.pre_crop.x * scaleX);
          const y = offsetY + (modelRoi.pre_crop.y * scaleY);
          const w = modelRoi.pre_crop.w * scaleX;
          const h = modelRoi.pre_crop.h * scaleY;

          // Рисуем зеленый прямоугольник с прозрачной заливкой
          ctx.strokeStyle = '#10b981';
          ctx.fillStyle = 'rgba(16, 185, 129, 0)';
          ctx.lineWidth = 3;
          ctx.setLineDash([]);
          ctx.fillRect(x, y, w, h);
          ctx.strokeRect(x, y, w, h);

          // Подпись модели (на русском, без пометки pre-crop)
          ctx.fillStyle = '#10b981';
          ctx.font = 'bold 14px Arial';
          ctx.fillText(getModelDisplayName(modelName), x + 5, y + 20);
        }

        // Рисуем post_filter (если есть)
        if (modelRoi.post_filter && Array.isArray(modelRoi.post_filter)) {
          modelRoi.post_filter.forEach((filter: any) => {
            if (filter && filter.x !== null && filter.y !== null) {
              const x = offsetX + (filter.x * scaleX);
              const y = offsetY + (filter.y * scaleY);
              const w = filter.w * scaleX;
              const h = filter.h * scaleY;

              // Рисуем зеленый прямоугольник с пунктирной рамкой
              ctx.strokeStyle = '#10b981';
              ctx.fillStyle = 'rgba(16, 185, 129, 0.15)';
              ctx.lineWidth = 2;
              ctx.setLineDash([5, 5]);
              ctx.fillRect(x, y, w, h);
              ctx.strokeRect(x, y, w, h);

              // Подпись модели (на русском, без пометки post-filter)
              ctx.fillStyle = '#10b981';
              ctx.font = 'bold 12px Arial';
              ctx.fillText(getModelDisplayName(modelName), x + 5, y + 15);
            }
          });
        }
      });

      if (hasPresenceZone) presenceZone.forEach((presenceZone) => {
        const x = offsetX + presenceZone.x * scaleX;
        const y = offsetY + presenceZone.y * scaleY;
        const w = presenceZone.w * scaleX;
        const h = presenceZone.h * scaleY;

        ctx.strokeStyle = '#f59e0b';
        ctx.fillStyle = 'rgba(245, 158, 11, 0.15)';
        ctx.lineWidth = 3;
        ctx.setLineDash([]);
        ctx.fillRect(x, y, w, h);
        ctx.strokeRect(x, y, w, h);

        ctx.fillStyle = '#f59e0b';
        ctx.font = 'bold 14px Arial';
        ctx.fillText(presenceZone.name, x + 5, y + 20);
      });

      // Вектор направления въезда (проценты от кадра)
      if (hasDirectionVector && directionVector) {
        const x1 = offsetX + (directionVector.x1 / 100) * videoDisplayWidth;
        const y1 = offsetY + (directionVector.y1 / 100) * videoDisplayHeight;
        const x2 = offsetX + (directionVector.x2 / 100) * videoDisplayWidth;
        const y2 = offsetY + (directionVector.y2 / 100) * videoDisplayHeight;
        drawDirectionArrow(ctx, x1, y1, x2, y2, { label: 'Въезд' });
      }
    };

    // Обновляем размеры при загрузке и изменении размера
    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);

    // Периодически обновляем оверлей (на случай изменения ROI)
    const intervalId = setInterval(() => {
      updateCanvasSize();
    }, 2000);

    return () => {
      window.removeEventListener('resize', updateCanvasSize);
      clearInterval(intervalId);
      clear();
    };
  }, [
    activeCamera?.status?.roi,
    attachedCameraModels,
    activeCameraId,
    videoDimensions.width,
    videoDimensions.height,
    cameras,
    presenceMonitoringEnabled,
    presenceZone,
    activeHasLicensePlateModel,
    directionVector,
  ]);

  // Инициализация основного видео-плеера (WebRTC через iframe)
  useEffect(() => {
    const container = mainVideoRef.current;
    if (!container) return;

    // Удаляем старый iframe если есть
    if (mainIframeRef.current && mainIframeRef.current.parentNode) {
      mainIframeRef.current.parentNode.removeChild(mainIframeRef.current);
      mainIframeRef.current = null;
    }

    // Неоплаченной камере поток не подключаем — вместо видео плашка «Оплатить»
    if (
      isCameraWaiting(activeCamera) ||
      !activeCamera?.webrtc_out ||
      activeCamera.is_paid === false ||
      activeNeedsLprRoi
    ) {
      return;
    }

    let streamUrl = activeCamera.webrtc_out;
    if (streamUrl.startsWith('https://') && streamUrl.includes('192.168.')) {
      streamUrl = streamUrl.replace('https://', 'http://');
    }

    // Создаем новый iframe
    const iframe = document.createElement('iframe');
    iframe.src = streamUrl;
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.style.border = 'none';
    iframe.style.borderRadius = '18px';
    iframe.allow = 'autoplay; fullscreen';
    iframe.setAttribute('allowfullscreen', 'true');
    mainIframeRef.current = iframe;
    container.appendChild(iframe);

    return () => {
      if (mainIframeRef.current && mainIframeRef.current.parentNode) {
        mainIframeRef.current.parentNode.removeChild(mainIframeRef.current);
      }
      mainIframeRef.current = null;
    };
  }, [
    activeCamera?.webrtc_out,
    activeCamera?.sync_status,
    activeCamera?.is_paid,
    activeNeedsLprRoi,
    activeCameraId,
    videoReloadToken,
  ]);

  const renderVideoBottomBar = () => {
    // Счётчик людей временно отключён
    // if (showPeopleCounterPanel) {
    //   return (
    //     <div className="area-cameras__video-bottom area-cameras__video-bottom--counter">
    //       <span className="area-cameras__counter-label">Счетчик людей:</span>
    //       <strong className="area-cameras__people-count-value">
    //         {peopleCount !== null ? peopleCount : '—'}
    //       </strong>
    //       {peopleWsConnected && <span className="area-cameras__live-dot" aria-hidden="true" />}
    //       {peopleWsError && (
    //         <span className="area-cameras__counter-ws-hint">нет связи</span>
    //       )}
    //     </div>
    //   );
    // }

    if (isCameraWaiting(activeCamera)) {
      return (
        <div className="area-cameras__video-bottom area-cameras__video-bottom--waiting">
          <span className="area-cameras__wait-dot" aria-hidden="true" />
          <span>Ожидание</span>
        </div>
      );
    }

    return (
      <div className="area-cameras__video-bottom">
        <span className="area-cameras__live-dot" aria-hidden="true" />
        <span>Живое видео</span>
      </div>
    );
  };

  /** Камера не оплачена (оплата закончилась или ещё не вносилась) — показываем плашку вместо видео. */
  const isCameraUnpaid = (camera?: { is_paid?: boolean; sync_status?: string | null } | null) =>
    !!camera && camera.is_paid === false && camera.sync_status !== 'pending_delete';

  const { confirm, alert: showAlert, dialog: confirmDialog } = useConfirm();
  const [payingCamera, setPayingCamera] = useState(false);
  const [payCameraError, setPayCameraError] = useState<{ message: string; lowBalance: boolean } | null>(null);

  const payForActiveCamera = async () => {
    if (!activeCamera?.id) return;
    const price = (user?.camera_monthly_price ?? 1000).toLocaleString('ru-RU');
    const ok = await confirm({
      title: 'Оплата камеры',
      highlight: (
        <>
          <span>Спишется с баланса</span>
          <strong>{price} ₽</strong>
        </>
      ),
      message: (
        <>
          Камера «{activeCamera.name}» будет оплачена на месяц, видео и аналитика включатся сразу после
          оплаты. На балансе сейчас {(user?.balance ?? 0).toLocaleString('ru-RU')} ₽.
        </>
      ),
      confirmText: 'Оплатить',
    });
    if (!ok) return;
    setPayCameraError(null);
    try {
      setPayingCamera(true);
      await cameraBillingAPI.renew(activeCamera.id);
      void refreshUser();
      await loadCameras();
      setVideoReloadToken((token) => token + 1);
    } catch (err: any) {
      setPayCameraError({
        message: err?.response?.message || err?.message || 'Не удалось оплатить камеру',
        lowBalance: isInsufficientFundsError(err),
      });
    } finally {
      setPayingCamera(false);
    }
  };

  const renderUnpaidPlaceholder = () => (
    <div className="area-cameras__video-placeholder area-cameras__video-placeholder--unpaid">
      <span className="area-cameras__unpaid-title">
        {activeCamera?.paid_until ? 'Оплата камеры закончилась' : 'Камера не оплачена'}
      </span>
      <span className="area-cameras__unpaid-text">
        Камера сохранена со всеми настройками. Оплатите её, чтобы снова включить видео и аналитику.
      </span>
      <button
        type="button"
        className="ui-btn ui-btn--primary"
        onClick={() => void payForActiveCamera()}
        disabled={payingCamera}
      >
        {payingCamera
          ? 'Оплачиваем…'
          : `Оплатить ${(user?.camera_monthly_price ?? 1000).toLocaleString('ru-RU')} ₽ / месяц`}
      </button>
      {payCameraError && (
        <span className="area-cameras__unpaid-error">
          {payCameraError.message}
          {payCameraError.lowBalance && (
            <>
              {' '}
              <button type="button" className="area-modal__billing-link" onClick={() => navigate('/balance')}>
                Пополнить баланс
              </button>
            </>
          )}
        </span>
      )}
    </div>
  );

  /** Камера отключена во внешнем бэке (нет видеопотока) — данные и оплата сохранены. */
  const isCameraOffline = (camera?: { is_online?: boolean } | null) => !!camera && camera.is_online === false;

  const renderOfflinePlaceholder = () => (
    <div className="area-cameras__video-placeholder area-cameras__video-placeholder--unpaid">
      <span className="area-cameras__unpaid-title">Камера не в сети</span>
      <span className="area-cameras__unpaid-text">
        Нет видеопотока с камеры. Настройки и оплата сохранены — изображение появится, как только камера снова
        подключится.
      </span>
    </div>
  );

  const renderNeedsRoiPlaceholder = () => (
    <div className="area-cameras__video-placeholder area-cameras__video-placeholder--unpaid area-cameras__video-placeholder--roi">
      <span className="area-cameras__unpaid-title">Задайте зону распознавания номеров</span>
      <span className="area-cameras__unpaid-text">
        Без зоны ROI распознавание номеров работает неточно, поэтому камера пока выключена. Выделите на кадре
        область, где проезжают машины — зона должна быть больше автомобиля.
      </span>
      <button type="button" className="ui-btn ui-btn--primary" onClick={() => setIsRoiSelectorOpen(true)}>
        Задать зону ROI
      </button>
    </div>
  );

  const renderVideoWaitingPlaceholder = () => (
    <div className="area-cameras__video-placeholder area-cameras__video-placeholder--waiting">
      <span className="area-cameras__wait-spinner" aria-hidden="true" />
      <span className="area-cameras__wait-text">Подключение камеры…</span>
    </div>
  );

  const [isCameraFormOpen, setIsCameraFormOpen] = useState(false);
  const [cameraFormMode, setCameraFormMode] = useState<'add' | 'edit'>('add');
  const [editingCameraId, setEditingCameraId] = useState<number | null>(null);
  const [editingSourceId, setEditingSourceId] = useState<string | null>(null);
  const [initialEditModels, setInitialEditModels] = useState<string[]>([]);
  const [initialEditName, setInitialEditName] = useState('');
  const [initialEditRtsp, setInitialEditRtsp] = useState('');
  const [peopleCounterEnabled, setPeopleCounterEnabled] = useState(false);
  const [initialPeopleCounterEnabled, setInitialPeopleCounterEnabled] = useState(false);
  const [presenceCheckEnabled, setPresenceCheckEnabled] = useState(false);
  const [initialPresenceCheckEnabled, setInitialPresenceCheckEnabled] = useState(false);
  const [newCameraName, setNewCameraName] = useState('');
  const [newCameraRtsp, setNewCameraRtsp] = useState('');
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [isSubmittingCamera, setIsSubmittingCamera] = useState(false);
  const [cameraError, setCameraError] = useState<string>('');

  const resetCameraFormFields = () => {
    setAutoRenewNewCamera(false);
    setCameraFundsError(false);
    setNewCameraName('');
    setNewCameraRtsp('');
    setSelectedModels([]);
    setCameraError('');
    setCameraFormMode('add');
    setEditingCameraId(null);
    setEditingSourceId(null);
    setInitialEditModels([]);
    setInitialEditName('');
    setInitialEditRtsp('');
    setPeopleCounterEnabled(false);
    setInitialPeopleCounterEnabled(false);
    setPresenceCheckEnabled(false);
    setInitialPresenceCheckEnabled(false);
  };

  const selectedHasPeopleModel = useMemo(
    () => cameraHasPeopleCounterModel(selectedModels),
    [selectedModels]
  );

  const selectedHasPresenceModel = useMemo(
    () => cameraHasPresenceCheckModel(selectedModels),
    [selectedModels]
  );

  // Счётчик людей временно отключён — статус не запрашиваем
  // useEffect(() => {
  //   if (!activeCamera?.camera_id || !activeHasPeopleModel) {
  //     setPeopleCounterLiveEnabled(false);
  //     return;
  //   }
  //
  //   let cancelled = false;
  //   camerasAPI
  //     .getPeopleCounterStatus(activeCamera.camera_id)
  //     .then((res) => {
  //       if (!cancelled && res.success) {
  //         setPeopleCounterLiveEnabled(Boolean(res.enabled));
  //       }
  //     })
  //     .catch(() => {
  //       if (!cancelled) setPeopleCounterLiveEnabled(false);
  //     });
  //
  //   return () => {
  //     cancelled = true;
  //   };
  // }, [activeCamera?.camera_id, activeHasPeopleModel]);

  useEffect(() => {
    if (!activeCamera?.camera_id || !activeHasPresenceModel) {
      setPresenceMonitoringEnabled(false);
      setPresenceZone([]);
      return;
    }

    let cancelled = false;
    camerasAPI
      .getPresenceSettings(activeCamera.camera_id)
      .then((res) => {
        if (!cancelled && res.success) {
          const settings = normalizePresenceSettings(res.settings ?? res);
          setPresenceMonitoringEnabled(settings.enabled);
          setPresenceZone(settings.enabled ? settings.zones : []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPresenceMonitoringEnabled(false);
          setPresenceZone([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [activeCamera?.camera_id, activeHasPresenceModel]);

  // Вектор направления въезда — только для камер с распознаванием гос. номеров
  useEffect(() => {
    if (!activeCamera?.camera_id || !activeHasLicensePlateModel) {
      setDirectionVector(null);
      return;
    }

    let cancelled = false;
    camerasAPI
      .getDirectionVector(activeCamera.camera_id)
      .then((res) => {
        if (!cancelled) {
          setDirectionVector(
            res.success ? parseDirectionVector(res.vector, videoDimensions.width, videoDimensions.height) : null
          );
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDirectionVector(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [activeCamera?.camera_id, activeHasLicensePlateModel, videoDimensions.width, videoDimensions.height]);

  const closeCameraFormModal = () => {
    setIsCameraFormOpen(false);
    resetCameraFormFields();
  };

  const openAddCameraModal = () => {
    resetCameraFormFields();
    setCameraFormMode('add');
    setIsCameraFormOpen(true);
  };

  const openEditCameraModal = () => {
    if (!activeCamera?.id) return;
    resetCameraFormFields();
    setCameraFormMode('edit');
    setEditingCameraId(activeCamera.id);
    setEditingSourceId(activeCamera.camera_id);
    const m = [...(activeCamera.models ?? [])];
    setInitialEditModels(m);
    setSelectedModels(m);
    const rtspForForm =
      (activeCamera.rtsp_uri && activeCamera.rtsp_uri.trim()) ||
      (typeof activeCamera.status?.rtsp_uri === 'string' ? activeCamera.status.rtsp_uri.trim() : '') ||
      '';
    setInitialEditName(activeCamera.name);
    setInitialEditRtsp(rtspForForm);
    setNewCameraName(activeCamera.name);
    setNewCameraRtsp(rtspForForm);
    setIsCameraFormOpen(true);

    // Счётчик людей временно отключён
    // const hasPeople = cameraHasPeopleCounterModel(m);
    const hasPresence = cameraHasPresenceCheckModel(m);
    const extId = activeCamera.camera_id;
    // if (extId && hasPeople) {
    //   camerasAPI
    //     .getPeopleCounterStatus(extId)
    //     .then((res) => {
    //       if (res.success) {
    //         setPeopleCounterEnabled(Boolean(res.enabled));
    //         setInitialPeopleCounterEnabled(Boolean(res.enabled));
    //       }
    //     })
    //     .catch(() => {
    //       setPeopleCounterEnabled(false);
    //       setInitialPeopleCounterEnabled(false);
    //     });
    // } else {
    setPeopleCounterEnabled(false);
    setInitialPeopleCounterEnabled(false);
    // }

    if (extId && hasPresence) {
      camerasAPI
        .getPresenceSettings(extId)
        .then((res) => {
          if (res.success) {
            const settings = normalizePresenceSettings(res.settings ?? res);
            setPresenceCheckEnabled(settings.enabled);
            setInitialPresenceCheckEnabled(settings.enabled);
          }
        })
        .catch(() => {
          setPresenceCheckEnabled(false);
          setInitialPresenceCheckEnabled(false);
        });
    } else {
      setPresenceCheckEnabled(false);
      setInitialPresenceCheckEnabled(false);
    }
  };

  const modelsEditableInForm = cameraFormMode !== 'edit' || Boolean(editingSourceId);

  // Счётчик людей временно отключён
  // const applyPeopleCounterSetting = async (externalCameraId: string, enabled: boolean) => {
  //   if (enabled) {
  //     return camerasAPI.enablePeopleCounter(externalCameraId);
  //   }
  //   return camerasAPI.disablePeopleCounter(externalCameraId);
  // };

  const applyPresenceMonitoring = async (externalCameraId: string, enabled: boolean) => {
    if (enabled) {
      return camerasAPI.setPresenceSettings(externalCameraId, {
        enabled: true,
      });
    }
    return camerasAPI.setPresenceSettings(externalCameraId, { enabled: false });
  };

  const handleToggleModel = (modelName: string) => {
    setSelectedModels((prev) => {
      const next = prev.includes(modelName)
        ? prev.filter((m) => m !== modelName)
        : [...prev, modelName];
      if (!next.some(isPeopleCounterModel)) {
        setPeopleCounterEnabled(false);
      }
      if (!next.some(isPresenceCheckModel)) {
        setPresenceCheckEnabled(false);
      }
      return next;
    });
  };

  const handleCameraFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!area || !newCameraName.trim() || !newCameraRtsp.trim()) {
      setCameraError('Заполните все обязательные поля');
      return;
    }

    try {
      setIsSubmittingCamera(true);
      setCameraError('');

      if (cameraFormMode === 'edit' && editingCameraId != null) {
        const name = newCameraName.trim();
        const rtsp = newCameraRtsp.trim();
        const added = selectedModels.filter((m) => !initialEditModels.includes(m));
        const removed = initialEditModels.filter((m) => !selectedModels.includes(m));

        if (!editingSourceId && (added.length > 0 || removed.length > 0)) {
          setCameraError('Нельзя изменить модели: у камеры нет идентификатора (camera_id).');
          return;
        }

        const nameChanged = name !== initialEditName;
        const rtspChanged = rtsp !== initialEditRtsp;

        if (editingSourceId) {
          const patchBody: { name?: string; rtsp_uri?: string } = {};
          if (nameChanged) patchBody.name = name;
          if (rtsp) patchBody.rtsp_uri = rtsp;

          if (Object.keys(patchBody).length > 0) {
            const respPatch = await camerasAPI.patchBySourceId(editingSourceId, patchBody);
            if (!respPatch.success) {
              setCameraError(respPatch.message || 'Ошибка сохранения камеры');
              return;
            }
            if (respPatch.queued) {
              notifyNotificationsChanged();
              await loadCameras();
              setVideoReloadToken((token) => token + 1);
              closeCameraFormModal();
              return;
            }
          }
        } else if (nameChanged || rtspChanged) {
          const respUpdate = await camerasAPI.update(editingCameraId, name, rtsp);
          if (!respUpdate.success) {
            setCameraError(respUpdate.message || 'Ошибка сохранения камеры');
            return;
          }
        }

        if (editingSourceId) {
          for (const modelName of added) {
            const r = await camerasAPI.attachModel(editingSourceId, modelName);
            if (!r.success) {
              setCameraError(r.message || `Не удалось привязать модель: ${modelName}`);
              return;
            }
          }
          for (const modelName of removed) {
            const r = await camerasAPI.detachModel(editingSourceId, modelName);
            if (!r.success) {
              setCameraError(r.message || `Не удалось отвязать модель: ${modelName}`);
              return;
            }
          }

          // Счётчик людей временно отключён
          // const stillHasPeople = cameraHasPeopleCounterModel(selectedModels);
          const stillHasPresence = cameraHasPresenceCheckModel(selectedModels);
          // if (stillHasPeople && peopleCounterEnabled !== initialPeopleCounterEnabled) {
          //   const pc = await applyPeopleCounterSetting(editingSourceId, peopleCounterEnabled);
          //   if (!pc.success) {
          //     setCameraError(pc.message || 'Не удалось изменить счётчик людей');
          //     return;
          //   }
          //   setPeopleCounterLiveEnabled(peopleCounterEnabled);
          // } else if (!stillHasPeople && initialPeopleCounterEnabled) {
          //   const pc = await camerasAPI.disablePeopleCounter(editingSourceId);
          //   if (!pc.success) {
          //     setCameraError(pc.message || 'Не удалось отключить счётчик людей');
          //     return;
          //   }
          //   setPeopleCounterLiveEnabled(false);
          // }

          if (stillHasPresence && presenceCheckEnabled !== initialPresenceCheckEnabled) {
            const pr = await applyPresenceMonitoring(editingSourceId, presenceCheckEnabled);
            if (!pr.success) {
              setCameraError(pr.message || 'Не удалось изменить проверку присутствия');
              return;
            }
            if (pr.queued) notifyNotificationsChanged();
            setPresenceMonitoringEnabled(presenceCheckEnabled);
          } else if (!stillHasPresence && initialPresenceCheckEnabled) {
            const pr = await camerasAPI.setPresenceSettings(editingSourceId, { enabled: false });
            if (!pr.success) {
              setCameraError(pr.message || 'Не удалось отключить проверку присутствия');
              return;
            }
            if (pr.queued) notifyNotificationsChanged();
            setPresenceMonitoringEnabled(false);
          }
        }

        await loadCameras();
        setVideoReloadToken((token) => token + 1);
        closeCameraFormModal();
        return;
      }

      const response = await camerasAPI.create(
        newCameraName.trim(),
        newCameraRtsp.trim(),
        area.id,
        selectedModels,
        autoRenewNewCamera
      );

      if (response.success) {
        // С баланса списана оплата за первый месяц
        void refreshUser();
        if (response.queued) {
          notifyNotificationsChanged();
          await loadCameras();
          setVideoReloadToken((token) => token + 1);
          closeCameraFormModal();
          return;
        }

        const extId = response.camera?.camera_id;
        // Счётчик людей временно отключён
        // if (extId && cameraHasPeopleCounterModel(selectedModels) && peopleCounterEnabled) {
        //   const pc = await camerasAPI.enablePeopleCounter(extId);
        //   if (!pc.success) {
        //     setCameraError(pc.message || 'Камера создана, но не удалось включить счётчик людей');
        //     await loadCameras();
        //     return;
        //   }
        //   setPeopleCounterLiveEnabled(true);
        // }
        if (extId && cameraHasPresenceCheckModel(selectedModels) && presenceCheckEnabled) {
          const pr = await camerasAPI.setPresenceSettings(extId, {
            ...DEFAULT_PRESENCE_SETTINGS,
            enabled: true,
          });
          if (!pr.success) {
            setCameraError(pr.message || 'Камера создана, но не удалось включить проверку присутствия');
            await loadCameras();
            return;
          }
          if (pr.queued) notifyNotificationsChanged();
          setPresenceMonitoringEnabled(true);
        }

        await loadCameras();
        setVideoReloadToken((token) => token + 1);
        closeCameraFormModal();
      } else {
        console.error('Camera creation failed:', response);
        setCameraError(response.message || 'Ошибка добавления камеры');
      }
    } catch (err: any) {
      console.error('Camera form error:', err);
      if (isInsufficientFundsError(err)) {
        setCameraFundsError(true);
        setCameraError(err.response?.message || 'Недостаточно средств на балансе для оплаты камеры');
        void refreshUser();
        return;
      }
      setCameraFundsError(false);
      let errorMessage =
        err.message || err.response?.message || (cameraFormMode === 'edit' ? 'Ошибка сохранения камеры' : 'Ошибка добавления камеры');

      const rtspHint = err.response?.rtsp_hint as string | undefined;
      if (rtspHint) {
        errorMessage = `${errorMessage}\n\n${rtspHint}`;
      }

      setCameraError(errorMessage);
    } finally {
      setIsSubmittingCamera(false);
    }
  };

  // Показываем прелоадер только если авторизация уже прошла, но данные участка еще загружаются
  if (loading && !authLoading) {
    return <Loader fullScreen size="large" />;
  }

  // Если еще идет авторизация, ProtectedRoute покажет прелоадер
  if (authLoading) {
    return null;
  }

  if (error || !area) {
    return (
      <div className="area-page">
        <main className="area-main container">
          <div className="ui-card ui-empty">
            <span className="ui-empty__title">{error || 'Участок не найден'}</span>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="area-page">
      {confirmDialog}
      <main className="area-main container">
        <section className="area-cameras">
          <header className="area-cameras__header">
            <div className="area-cameras__title-row">
              <div className='area-cameras-title-container'>
                <h1 className="area-cameras__title">{area.name}</h1>
                {cameras.length > 0 && (
                  <select
                    className="area-cameras__select"
                    value={activeCameraId || ''}
                    onChange={(e) => {
                      setActiveCameraId(e.target.value);
                    }}
                    aria-label="Выбор камеры"
                  >
                    {cameras.map((c) => {
                      const optionValue = getCameraIdentifier(c);
                      return (
                        <option key={optionValue} value={optionValue}>
                          {c.name}
                        </option>
                      );
                    })}
                  </select>
                )}
              </div>
                
              <div className="area-cameras__controls">
                <button
                  className="area-cameras__icon-button"
                  type="button"
                  aria-label="Редактировать камеру"
                  title="Редактировать камеру"
                  onClick={() => {
                    if (!activeCamera?.id) return;
                    openEditCameraModal();
                  }}
                  disabled={!activeCamera?.id}
                >
                  <img src={`${process.env.PUBLIC_URL}/icons/edit.svg`} alt="" className="area-card__icon-image" />
                </button>
                <button
                  className="area-cameras__icon-button area-cameras__icon-button--danger"
                  type="button"
                  aria-label="Удалить камеру"
                  onClick={() => {
                    if (!activeCamera || activeCamera.id === null) return;
                    setIsDeleteCameraOpen(true);
                  }}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/icons/trash.svg`}
                    alt=""
                    className="area-card__icon-image"
                  />
                </button>

                {activeCamera && !activeCameraUnpaid && activeCamera.camera_id && attachedCameraModels.length > 0 && (
                  <button
                    className="area-cameras__icon-button area-cameras__icon-button--roi"
                    type="button"
                    onClick={() => setIsRoiSelectorOpen(true)}
                    aria-label="Выбрать зону ROI"
                    title="Выбрать зону отслеживания"
                  >
                    <img src={roiIconSvgUrl} alt="" className="area-card__icon-image" />
                  </button>
                )}

                {activeCamera && !activeCameraUnpaid && !activeNeedsLprRoi && activeCamera.camera_id && activeHasLicensePlateModel && (
                  <button
                    className="area-cameras__icon-button area-cameras__icon-button--direction-vector"
                    type="button"
                    onClick={() => setIsDirectionVectorOpen(true)}
                    aria-label="Выбрать вектор направления въезда"
                    title="Вектор направления въезда"
                  >
                    <img src={directionVectorIconSvgUrl} alt="" className="area-card__icon-image" />
                  </button>
                )}

                {activeCamera && !activeCameraUnpaid && !activeNeedsLprRoi && activeCamera.camera_id && activeHasLicensePlateModel && (
                  <button
                    className="area-cameras__icon-button area-cameras__icon-button--barrier"
                    type="button"
                    onClick={() => setIsBarrierOpen(true)}
                    aria-label="Настройки шлагбаума"
                    title="Шлагбаум"
                  >
                    <img src={barrierIconSvgUrl} alt="" className="area-card__icon-image" />
                  </button>
                )}

                {activeCamera && !activeCameraUnpaid && !activeNeedsLprRoi && activeCamera.camera_id && activeHasLicensePlateModel && (
                  <button
                    className="area-cameras__icon-button area-cameras__icon-button--plates"
                    type="button"
                    onClick={() => setIsAreaPlatesOpen(true)}
                    aria-label="База номеров участка"
                    title="База номеров участка"
                  >
                    <img src={platesIconSvgUrl} alt="" className="area-card__icon-image" />
                  </button>
                )}

                {activeCamera && !activeCameraUnpaid && !activeNeedsLprRoi && activeCamera.camera_id && activeHasLicensePlateModel && (
                  <button
                    className="area-cameras__icon-button area-cameras__icon-button--residents"
                    type="button"
                    onClick={() => setIsResidentsOpen(true)}
                    aria-label="Пользователи участка и гостевые пропуска"
                    title="Пользователи и гости"
                  >
                    <img src={residentsIconSvgUrl} alt="" className="area-card__icon-image" />
                  </button>
                )}

                {activeCamera &&
                  !activeCameraUnpaid &&
                  activeCamera.camera_id &&
                  activeHasPresenceModel &&
                  presenceMonitoringEnabled && (
                    <button
                      className="area-cameras__icon-button area-cameras__icon-button--presence"
                      type="button"
                      onClick={() => setIsPresenceSettingsOpen(true)}
                      aria-label="Настройки контроля присутствия"
                      title="Контроль присутствия"
                    >
                      <img src={presenceIconSvgUrl} alt="" className="area-card__icon-image" />
                    </button>
                  )}

                <button
                  className="area-cameras__add"
                  type="button"
                  onClick={openAddCameraModal}
                >
                  <span className="area-cameras__add-plus">+</span>
                  <span>Добавить камеру</span>
                </button>
              </div>
            </div>
            {area.description && (
              <p className="area-cameras__description">{area.description}</p>
            )}
          </header>

          {camerasLoading ? (
            <div className="ui-card ui-loading">Загрузка камер...</div>
          ) : cameras.length === 0 ? (
            <div className="ui-card ui-empty area-cameras__empty">
              <span className="ui-empty__icon">
                <img
                  src={cameraIconSvgUrl}
                  alt=""
                  width={24}
                  height={24}
                  aria-hidden={true}
                />
              </span>
              <span className="ui-empty__title">Камера еще не добавлена</span>
              <span>Добавьте первую камеру, чтобы начать мониторинг</span>
              <button type="button" className="ui-btn ui-btn--primary" onClick={openAddCameraModal}>
                Добавить камеру
              </button>
            </div>
          ) : cameras.length === 1 ? (
            // Если только одна камера - занимает полную ширину
            <div className="area-cameras__single">
              <div className="area-cameras__video" ref={mainVideoRef}>
                {isCameraUnpaid(activeCamera)
                  ? renderUnpaidPlaceholder()
                  : isCameraOffline(activeCamera)
                    ? renderOfflinePlaceholder()
                    : activeNeedsLprRoi
                      ? renderNeedsRoiPlaceholder()
                      : isCameraWaiting(activeCamera) && renderVideoWaitingPlaceholder()}

                <canvas
                  ref={roiOverlayCanvasRef}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    pointerEvents: 'none',
                    width: '100%',
                    height: '100%',
                    zIndex: 2,
                  }}
                />

                <div className="area-cameras__video-overlay">
                  <div className="area-cameras__video-top">
                    <span className="area-cameras__video-label">{activeCamera?.name}</span>
                    {activeCamera?.delete_at && (
                      <span className="area-cameras__video-label area-cameras__video-label--deleting">
                        Удалится {formatBillingDate(activeCamera.delete_at)}
                      </span>
                    )}
                  </div>

                  {renderVideoBottomBar()}
                </div>
              </div>
            </div>
          ) : (
            // Если есть несколько камер - layout с основной слева и миниатюрами справа
            <div className="area-cameras__layout">
              <article className="area-cameras__main">
                <div className="area-cameras__video" ref={mainVideoRef}>
                  {isCameraUnpaid(activeCamera)
                  ? renderUnpaidPlaceholder()
                  : isCameraOffline(activeCamera)
                    ? renderOfflinePlaceholder()
                    : activeNeedsLprRoi
                      ? renderNeedsRoiPlaceholder()
                      : isCameraWaiting(activeCamera) && renderVideoWaitingPlaceholder()}

                  <canvas
                    ref={roiOverlayCanvasRef}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      pointerEvents: 'none',
                      width: '100%',
                      height: '100%',
                      zIndex: 2,
                    }}
                  />

                  <div className="area-cameras__video-overlay">
                    <div className="area-cameras__video-top">
                      <span className="area-cameras__video-label">{activeCamera?.name}</span>
                    {activeCamera?.delete_at && (
                      <span className="area-cameras__video-label area-cameras__video-label--deleting">
                        Удалится {formatBillingDate(activeCamera.delete_at)}
                      </span>
                    )}
                    </div>

                    {renderVideoBottomBar()}
                  </div>
                </div>
              </article>

              <aside className="area-cameras__grid" aria-label="Список камер">
                {cameras.filter(c => {
                  return getCameraIdentifier(c) !== String(activeCameraId);
                }).map((c) => {
                  const isActive = getCameraIdentifier(c) === String(activeCameraId);
                  const waiting = isCameraWaiting(c);
                  const unpaid = c.is_paid === false;
                  const needsRoi = cameraNeedsLprRoi(c);

                  // Неоплаченная камера: поток не грузим, показываем плашку
                  let thumbStreamUrl: string | null = waiting || unpaid || needsRoi ? null : (c.webrtc_out ?? null);
                  if (thumbStreamUrl && thumbStreamUrl.startsWith('https://') && thumbStreamUrl.includes('192.168.')) {
                    thumbStreamUrl = thumbStreamUrl.replace('https://', 'http://');
                  }

                  return (
                    <button
                      key={getCameraIdentifier(c)}
                      className={`area-cameras__thumb ${isActive ? 'area-cameras__thumb--active' : ''}`}
                      type="button"
                      onClick={() => setActiveCameraId(getCameraIdentifier(c))}
                      aria-label={`Открыть ${c.name}`}
                    >
                      <div className="area-cameras__thumb-title area-cameras__thumb-title--top">
                        {c.name}
                      </div>
                      <div className="area-cameras__thumb-media" aria-hidden="true">
                        {unpaid ? (
                          <div className="area-cameras__thumb-placeholder area-cameras__thumb-placeholder--unpaid">
                            {c.paid_until ? 'Оплата закончилась' : 'Не оплачена'}
                          </div>
                        ) : c.is_online === false ? (
                          <div className="area-cameras__thumb-placeholder area-cameras__thumb-placeholder--unpaid">
                            Не в сети
                          </div>
                        ) : needsRoi ? (
                          <div className="area-cameras__thumb-placeholder area-cameras__thumb-placeholder--unpaid">
                            Нужна зона ROI
                          </div>
                        ) : thumbStreamUrl ? (
                          <iframe
                            src={thumbStreamUrl}
                            title={c.name}
                            loading="lazy"
                            allow="autoplay; fullscreen"
                            allowFullScreen
                            tabIndex={-1}
                          />
                        ) : (
                          <div className="area-cameras__thumb-placeholder area-cameras__thumb-placeholder--waiting">
                            <span className="area-cameras__wait-spinner area-cameras__wait-spinner--sm" />
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </aside>
            </div>
          )}

          {isDeleteCameraOpen && activeCamera && activeCamera.id !== null && (
            <div className="area-modal" role="dialog" aria-modal="true">
              <div
                className="area-modal__backdrop"
                onClick={() => setIsDeleteCameraOpen(false)}
              />
              <div className="area-modal__dialog area-modal__dialog--small">
                <header className="area-modal__header">
                  <h2 className="area-modal__title">Удалить камеру</h2>
                  <button
                    className="area-modal__close"
                    type="button"
                    onClick={() => setIsDeleteCameraOpen(false)}
                    aria-label="Закрыть"
                  >
                    ×
                  </button>
                </header>

                <div className="area-modal__confirm-text">
                  {activeCamera.delete_at ? (
                    <>
                      Камера «{activeCamera.name}» уже поставлена на удаление и удалится{' '}
                      {formatBillingDate(activeCamera.delete_at)}, когда закончится оплаченный период.
                    </>
                  ) : activeCamera.is_paid ? (
                    <>
                      Камера «{activeCamera.name}» оплачена до {formatBillingDate(activeCamera.paid_until)}.
                      Она продолжит работать до конца оплаченного периода и удалится{' '}
                      {formatBillingDate(activeCamera.paid_until)}. Автопродление будет выключено.
                    </>
                  ) : (
                    <>Вы уверены, что хотите удалить камеру «{activeCamera.name}»?</>
                  )}
                </div>

                <footer className="area-modal__footer">
                  {activeCamera.delete_at ? (
                  <button
                    type="button"
                    className="area-modal__submit"
                    onClick={async () => {
                      if (!activeCamera || activeCamera.id === null) return;
                      try {
                        await cameraBillingAPI.cancelDelete(activeCamera.id);
                        setIsDeleteCameraOpen(false);
                        await loadCameras();
                      } catch (err: any) {
                        void showAlert({
                          title: 'Не получилось',
                          message: err?.response?.message || 'Не удалось отменить удаление камеры',
                        });
                      }
                    }}
                  >
                    Отменить удаление
                  </button>
                  ) : activeCamera.is_paid ? (
                  <button
                    type="button"
                    className="area-modal__submit area-modal__submit--danger"
                    onClick={async () => {
                      if (!activeCamera || activeCamera.id === null) return;
                      // Оплаченная камера не удаляется сразу — остаётся в списке с датой удаления
                      try {
                        const del = await camerasAPI.delete(activeCamera.id);
                        setIsDeleteCameraOpen(false);
                        await loadCameras();
                        if (del.scheduled && del.message) {
                          void showAlert({ title: 'Камера поставлена на удаление', message: del.message });
                        }
                      } catch (err: any) {
                        console.error('Ошибка удаления камеры', err);
                        void showAlert({
                          title: 'Не получилось',
                          message: err?.response?.message || 'Не удалось удалить камеру. Попробуйте ещё раз.',
                        });
                      }
                    }}
                  >
                    Удалить после окончания оплаты
                  </button>
                  ) : (
                  <button
                    type="button"
                    className="area-modal__submit area-modal__submit--danger"
                    onClick={async () => {
                      if (!activeCamera || activeCamera.id === null) return;

                      // Оптимистично убираем камеру из UI сразу, чтобы не было ощущения "зависло"
                      const deletedId = activeCamera.id;
                      const prevCameras = cameras;
                      const prevActiveCameraId = activeCameraId;

                      setIsDeleteCameraOpen(false);
                      setActiveCameraId(null);
                      setCameras((prev) => prev.filter((c) => c.id !== deletedId));

                      try {
                        const del = await camerasAPI.delete(activeCamera.id);
                        if (del.queued) {
                          notifyNotificationsChanged();
                        }
                        await loadCameras();
                      } catch (err) {
                        console.error('Ошибка удаления камеры', err);
                        // Откат в случае ошибки
                        setCameras(prevCameras);
                        setActiveCameraId(prevActiveCameraId);
                        void showAlert({
                          title: 'Не получилось',
                          message: 'Не удалось удалить камеру. Попробуйте ещё раз.',
                        });
                      }
                    }}
                  >
                    Удалить камеру
                  </button>
                  )}
                </footer>
              </div>
            </div>
          )}

          {isRoiSelectorOpen && activeCamera && (
            <RoiSelector
              camera={activeCamera}
              cameraModels={attachedCameraModels}
              initialModel={activeNeedsLprRoi && activeLprModel ? activeLprModel : undefined}
              onClose={() => {
                setIsRoiSelectorOpen(false);
              }}
              onUpdated={async (result) => {
                const cameraDbId = activeCamera.id;
                if (result && cameraDbId != null && findLicensePlateModel([result.model])) {
                  setLprRoiOverride((prev) => ({ ...prev, [cameraDbId]: !result.cleared }));
                }
                await loadCameras();
                setVideoReloadToken((token) => token + 1);
              }}
            />
          )}

          {isPresenceSettingsOpen && activeCamera && (
            <PresenceSettingsModal
              camera={activeCamera}
              onClose={() => setIsPresenceSettingsOpen(false)}
              onUpdated={async (zone) => {
                if (presenceMonitoringEnabled) {
                  setPresenceZone(zone);
                }
                await loadCameras();
                setVideoReloadToken((token) => token + 1);
              }}
            />
          )}

          {isBarrierOpen && activeCamera && (
            <BarrierModal
              camera={activeCamera}
              onClose={() => setIsBarrierOpen(false)}
            />
          )}

          {isResidentsOpen && area && (
            <ResidentsModal
              areaId={area.id}
              areaName={area.name}
              onClose={() => setIsResidentsOpen(false)}
            />
          )}

          {isAreaPlatesOpen && area && (
            <AreaPlatesModal
              areaId={area.id}
              areaName={area.name}
              onClose={() => setIsAreaPlatesOpen(false)}
            />
          )}

          {isDirectionVectorOpen && activeCamera && (
            <DirectionVectorSelector
              camera={activeCamera}
              currentVector={directionVector}
              onClose={() => setIsDirectionVectorOpen(false)}
              onUpdated={(vector) => {
                setDirectionVector(vector);
              }}
            />
          )}
        </section>

        {isPresenceStatsOpen && area && presenceStatsCameras.length > 0 && <PresenceStatsModal cameras={presenceStatsCameras} areaName={area.name} onClose={() => setIsPresenceStatsOpen(false)} />}

        {isDetectionsStatsOpen && area && (
          <DetectionsStatsModal
            areaId={area.id}
            areaName={area.name}
            cameraFilterId={logsCameraFilterId}
            cameraFilterName={
              logsCameraFilterId != null
                ? cameras.find((c) => c.id === logsCameraFilterId)?.name ?? null
                : null
            }
            onClose={() => setIsDetectionsStatsOpen(false)}
          />
        )}

        <section className="area-logs" aria-label="Детекции">
          <header className="area-logs__header">
            <h2 className="area-logs__title">
              Детекции <span className="area-logs__count">({logsTotal})</span>
            </h2>

            <div className="area-logs__controls" role="group" aria-label="Фильтры детекций">
              <button
                type="button"
                className="area-logs__stats-btn"
                onClick={() => setIsDetectionsStatsOpen(true)}
                aria-label="Статистика детекций за день и неделю"
              >
                <span className="area-logs__stats-btn-icon" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M4 19V5M10 19V9M16 19V13M22 19V7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
                <span>Статистика</span>
              </button>

              {presenceStatsCameras.length > 0 && <button type="button" className="area-logs__stats-btn" onClick={() => setIsPresenceStatsOpen(true)}>
                <span className="area-logs__stats-btn-icon" aria-hidden="true"><img src={presenceIconSvgUrl} width={18} height={18} alt="" /></span>
                <span>Статистика присутствия</span>
              </button>}

              <label className="area-logs__control">
                <span className="sr-only">Камера</span>
                <select
                  className="area-logs-card__camera-select"
                  value={logsCameraFilterId === null ? '' : String(logsCameraFilterId)}
                  onChange={(e) => {
                    const v = e.target.value;
                    setLogsCameraFilterId(v === '' ? null : Number(v));
                    setLogsModelFilter(null);
                  }}
                  aria-label="Фильтр детекций по камере"
                >
                  <option value="">Все детекции</option>
                  {cameras
                    .filter((c): c is (typeof cameras)[number] & { id: number } => typeof c.id === 'number')
                    .map((c) => (
                      <option key={c.id} value={String(c.id)}>
                        {c.name}
                      </option>
                    ))}
                </select>
              </label>

              <label className="area-logs__control">
                <span className="sr-only">Модель</span>
                <select
                  className="area-logs-card__camera-select"
                  value={logsModelFilter ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    setLogsModelFilter(v === '' ? null : v);
                  }}
                  aria-label="Фильтр детекций по модели"
                >
                  <option value="">Все модели</option>
                  {modelOptionsForArea.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>

              <div className="area-logs__range" role="group" aria-label="Период">
                <span className="sr-only">Период: выберите две даты в календаре</span>
                <LogsDateRangePicker
                  dateFrom={logsDateFrom}
                  dateTo={logsDateTo}
                  onChange={(from, to) => {
                    setLogsDateFrom(from);
                    setLogsDateTo(to);
                  }}
                />
              </div>
            </div>
          </header>

          {cameras.length > 0 && (
            <article className="area-logs-card">
              <div className="area-logs-card__head">
                <h3 className="area-logs-card__title">Детекции участка</h3>
              </div>

            {logsLoading ? (
              <div className="ui-loading">Загрузка детекций...</div>
            ) : logs.length === 0 ? (
              <div className="ui-empty">
                <span className="ui-empty__title">Детекции не найдены</span>
                <span>Измените фильтры или период, чтобы увидеть события</span>
              </div>
            ) : (
              <div className="area-logs-table">
                <div className="area-logs-table__head" role="row">
                  <button
                    type="button"
                    className="area-logs-table__sort area-logs-table__cell area-logs-table__cell--muted"
                    onClick={() => toggleLogsSort('timestamp')}
                    aria-label={`Сортировка по времени: сейчас ${logsSort.key === 'timestamp' ? (logsSort.dir === 'asc' ? 'по возрастанию' : 'по убыванию') : 'не выбрано'}`}
                  >
                    Время
                    <span className="area-logs-table__sort-icon" aria-hidden>
                      {logsSort.key === 'timestamp' && (
                        <span
                          className={
                            'area-logs-table__chev' + (logsSort.dir === 'asc' ? ' area-logs-table__chev--up' : '')
                          }
                        >
                          <svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                            <path
                              d="M5 8l5 5 5-5"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                      )}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="area-logs-table__sort area-logs-table__cell area-logs-table__cell--muted"
                    onClick={() => toggleLogsSort('model')}
                    aria-label={`Сортировка по модели: сейчас ${logsSort.key === 'model' ? (logsSort.dir === 'asc' ? 'по возрастанию' : 'по убыванию') : 'не выбрано'}`}
                  >
                    Модель
                    <span className="area-logs-table__sort-icon" aria-hidden>
                      {logsSort.key === 'model' && (
                        <span
                          className={
                            'area-logs-table__chev' + (logsSort.dir === 'asc' ? ' area-logs-table__chev--up' : '')
                          }
                        >
                          <svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                            <path
                              d="M5 8l5 5 5-5"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                      )}
                    </span>
                  </button>
                  <div className="area-logs-table__cell area-logs-table__cell--muted" role="columnheader">
                    Детекция
                  </div>
                </div>

                {sortedLogs.map((log, idx) => (
                  <div key={log.id ?? `${log.timestamp}-${idx}`} className="area-logs-table__row" role="row">
                    <div className="area-logs-table__cell" role="cell">
                      {log.timestamp}
                    </div>
                    <div className="area-logs-table__cell area-logs-table__cell--model" role="cell">
                      <span>{getModelDisplayName(log.model)}</span>
                      {(log.model === 'absence' || log.model === 'absence_end') && <span>{log.zone_name ?? 'Весь кадр'} · {log.absence_duration_sec ?? '—'} с</span>}
                      {log.direction != null && <DirectionBadge direction={log.direction} />}
                    </div>
                    <div className="area-logs-table__cell" role="cell">
                      {log.screen_url ? (
                        <button
                          type="button"
                          className="area-logs-card__photo-btn"
                          aria-label="Просмотр"
                          onClick={() => {
                            setSelectedPhotoUrl(log.screen_url);
                            setSelectedPhotoMeta({
                              label: getModelDisplayName(log.model),
                              direction: log.direction ?? null,
                            });
                            setIsPhotoModalOpen(true);
                          }}
                        >
                          <img
                            src={cameraIconSvgUrl}
                            alt=""
                            width={18}
                            height={18}
                            className="area-logs-card__photo-btn-icon"
                            aria-hidden={true}
                          />
                          <span className="area-logs-card__photo-btn-text">Просмотр</span>
                        </button>
                      ) : (
                        <span className="area-logs-table__dash">—</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {logs.length > 0 && logs.length < logsTotal && (
              <button 
                className="area-logs-card__more" 
                type="button"
                onClick={loadMoreLogs}
                disabled={logsLoadingMore}
              >
                {logsLoadingMore ? (
                  'Загрузка...'
                ) : (
                  `Показать еще (${logsTotal - logs.length} осталось)`
                )}
              </button>
            )}
            </article>
          )}
        </section>
      </main>

      {/* Модалка просмотра фотофиксации */}
      {isPhotoModalOpen && selectedPhotoUrl && (
        <div className="area-modal" role="dialog" aria-modal="true">
          <div
            className="area-modal__backdrop"
            onClick={() => setIsPhotoModalOpen(false)}
          />
          <div className="area-modal__dialog area-modal__dialog--photo">
            <header className="area-modal__header">
              <h2 className="area-modal__title">Фотофиксация детекции</h2>
              <button
                className="area-modal__close"
                type="button"
                onClick={() => setIsPhotoModalOpen(false)}
                aria-label="Закрыть"
              >
                ×
              </button>
            </header>

            <div className="area-modal__photo-body">
              {selectedPhotoLoading && (
                <div className="area-modal__photo-status">Загрузка изображения…</div>
              )}
              {selectedPhotoError && !selectedPhotoLoading && (
                <div className="area-modal__photo-status area-modal__photo-status--error">
                  Ошибка загрузки изображения
                </div>
              )}
              {selectedPhotoBlobUrl && !selectedPhotoLoading && !selectedPhotoError && (
                <img
                  src={selectedPhotoBlobUrl}
                  alt="Фотофиксация"
                  className="area-modal__photo-img"
                  onError={onSelectedPhotoImageError}
                />
              )}
            </div>

            {selectedPhotoMeta && (
              <div className="area-modal__photo-meta">
                <span className="area-modal__photo-meta-label">
                  {selectedPhotoMeta.direction != null ? 'Номер' : 'Модель'}
                </span>
                <span className="area-modal__photo-meta-value">{selectedPhotoMeta.label}</span>
                {selectedPhotoMeta.direction && (
                  <>
                    <span className="area-modal__photo-meta-label">Направление</span>
                    <DirectionBadge direction={selectedPhotoMeta.direction} />
                  </>
                )}
              </div>
            )}

            <footer className="area-modal__footer">
              <button
                type="button"
                className="area-modal__submit area-modal__submit--secondary"
                onClick={() => setIsPhotoModalOpen(false)}
              >
                Закрыть
              </button>
            </footer>
          </div>
        </div>
      )}

      {isCameraFormOpen && (
        <div className="area-modal" role="dialog" aria-modal="true">
          <div
            className="area-modal__backdrop"
            onClick={closeCameraFormModal}
          />
          <div className="area-modal__dialog">
            <header className="area-modal__header">
              <h2 className="area-modal__title">
                {cameraFormMode === 'edit' ? 'Редактировать камеру' : 'Добавить новую камеру'}
              </h2>
              <button
                type="button"
                className="area-modal__close"
                aria-label="Закрыть"
                onClick={closeCameraFormModal}
              >
                ×
              </button>
            </header>

            <form className="area-modal__body" onSubmit={handleCameraFormSubmit}>
              <div className="area-modal__left">
                <div className="area-modal__field">
                  <input
                    className="area-modal__input"
                    type="text"
                    placeholder="Название камеры"
                    value={newCameraName}
                    onChange={(e) => setNewCameraName(e.target.value)}
                    required
                  />
                </div>
                <div className="area-modal__field">
                  <input
                    className="area-modal__input"
                    type="url"
                    placeholder="RTSP URL камеры"
                    value={newCameraRtsp}
                    onChange={(e) => setNewCameraRtsp(e.target.value)}
                    required
                  />
                </div>
                <div className="area-modal__field">
                  <input
                    className="area-modal__input"
                    type="text"
                    placeholder="Участок"
                    value={area?.name || ''}
                    disabled
                  />
                </div>
                {(selectedHasPeopleModel || selectedHasPresenceModel) && (
                  <div className="area-modal__people-options">
                    {/* Счётчик людей временно отключён
                    {selectedHasPeopleModel && (
                      <label className="area-modal__people-counter-option">
                        <input
                          type="checkbox"
                          checked={peopleCounterEnabled}
                          onChange={(e) => setPeopleCounterEnabled(e.target.checked)}
                        />
                        <span className="area-modal-analytic__checkbox" aria-hidden="true" />
                        <span>Включить счётчик людей</span>
                      </label>
                    )}
                    */}
                    {selectedHasPresenceModel && (
                      <label className="area-modal__people-counter-option">
                        <input
                          type="checkbox"
                          checked={presenceCheckEnabled}
                          onChange={(e) => setPresenceCheckEnabled(e.target.checked)}
                        />
                        <span className="area-modal-analytic__checkbox" aria-hidden="true" />
                        <span>Проверка присутствия</span>
                      </label>
                    )}
                  </div>
                )}
                {cameraFormMode === 'edit' && !editingSourceId && (
                  <p className="area-modal__note">
                    Изменение списка моделей недоступно: у камеры нет идентификатора источника.
                  </p>
                )}

                {cameraFormMode === 'add' && (
                  <div className="area-modal__billing">
                    <div className="area-modal__billing-row">
                      <span>Стоимость камеры</span>
                      <strong>{cameraMonthlyPrice.toLocaleString('ru-RU')} ₽ / месяц</strong>
                    </div>
                    <div className="area-modal__billing-row">
                      <span>На балансе</span>
                      <strong>{userBalance.toLocaleString('ru-RU')} ₽</strong>
                    </div>
                    <p className="area-modal__billing-note">
                      Оплата за первый месяц спишется с баланса при добавлении камеры.
                    </p>
                    <label className="area-modal__people-counter-option">
                      <input
                        type="checkbox"
                        checked={autoRenewNewCamera}
                        onChange={(e) => setAutoRenewNewCamera(e.target.checked)}
                      />
                      <span className="area-modal-analytic__checkbox" aria-hidden="true" />
                      <span>Автопродление с баланса</span>
                    </label>
                    {!canAffordCamera && (
                      <div className="area-modal__billing-warning">
                        Недостаточно средств: нужно {cameraMonthlyPrice.toLocaleString('ru-RU')} ₽.{' '}
                        <button type="button" className="area-modal__billing-link" onClick={() => navigate('/balance')}>
                          Пополнить баланс
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="area-modal__right">
                <h3 className="area-modal__subtitle">Аналитики:</h3>

                <div className="area-modal__analytics-list">
                  {modelsLoading ? (
                    <div className="ui-loading">Загрузка моделей...</div>
                  ) : availableModels.length === 0 ? (
                    <div className="ui-empty">
                      <span>Нет доступных моделей</span>
                    </div>
                  ) : (
                    availableModels.map((model) => {
                      // Определяем иконку по названию модели
                      let icon = 'lock.svg';
                      const modelKey = (model || '').trim().toLowerCase();

                      if (modelKey.includes('license_plate') || modelKey.includes('plate')) {
                        // Распознавание номеров
                        icon = 'lock.svg';
                      } else if (modelKey === 'fire_and_smoke_detector') {
                        icon = 'fire-smoke.svg';
                      } else if (modelKey === 'fire') {
                        icon = 'fire-smoke.svg';
                      } else if (modelKey === 'smoke') {
                        icon = 'smoke.svg';
                      } else if (
                        modelKey.includes('multiple_rtsp') ||
                        ['car', 'vehicle', 'bus', 'truck', 'police', 'ambulance', 'firetruck', 'emergency_car_detector'].includes(
                          modelKey
                        )
                      ) {
                        // Машины
                        icon = 'car.svg';
                      } else if (modelKey === 'people' || modelKey === 'person' || modelKey === 'utrau_staff_detector') {
                        icon = 'people.svg';
                      } else if (
                        modelKey === 'smoking' ||
                        modelKey === 'smoking_detector' ||
                        modelKey === 'smoking_detector_v2' ||
                        modelKey === 'cigarette'
                      ) {
                        icon = 'kyrenie.svg';
                      } else if (modelKey === 'garbage_truck_detector' || modelKey === 'garbage_truck') {
                        icon = 'mysor.svg';
                      } else if (modelKey === 'tractor_detector') {
                        icon = 'tractor.svg';
                      }

                      // Выводим переведенное название модели
                      const label = modelTitles[model] || getModelDisplayName(model);

                      return (
                        <label key={model} className="area-modal-analytic">
                          <span className="area-modal-analytic__icon">
                            <img src={`${modalIconsBase}/${icon}`} alt="" />
                          </span>
                          <span className="area-modal-analytic__text">{label}</span>
                          <input
                            type="checkbox"
                            checked={selectedModels.includes(model)}
                            disabled={!modelsEditableInForm}
                            onChange={() => handleToggleModel(model)}
                          />
                          <span className="area-modal-analytic__checkbox" aria-hidden="true" />
                        </label>
                      );
                    })
                  )}
                </div>
              </div>

              {cameraError && (
                <div className="ui-alert ui-alert--danger area-modal__error" role="alert">
                  {cameraError}
                  {cameraFundsError && (
                    <>
                      {' '}
                      <button type="button" className="area-modal__billing-link" onClick={() => navigate('/balance')}>
                        Пополнить баланс
                      </button>
                    </>
                  )}
                </div>
              )}

              <footer className="area-modal__footer">
                <button 
                  type="submit" 
                  className="area-modal__submit"
                  disabled={isSubmittingCamera || modelsLoading || (cameraFormMode === 'add' && !canAffordCamera)}
                >
                  <span className="area-modal__submit-plus">+</span>
                  <span>
                    {isSubmittingCamera
                      ? cameraFormMode === 'edit'
                        ? 'Сохранение...'
                        : 'Добавление...'
                      : cameraFormMode === 'edit'
                        ? 'Сохранить'
                        : 'Добавить камеру'}
                  </span>
                </button>
              </footer>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default Area;

